import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useAuth } from "./useAuth";
import {
  getPayrollConfigs, getBonusConfig, getTaxConfig, getCompanyCostConfig,
} from "../firebase/services";
import { resolvePayrollConfig, DEFAULT_RULES } from "../firebase/schema";
import { setClosingDay } from "../pages/promoter/PromoterSales";

// Cấu hình tập trung, tải một lần sau khi đăng nhập.
// Mọi trang hỏi cfgFor(tháng) để lấy đúng bộ quy tắc áp dụng cho tháng đó.
const PayrollConfigContext = createContext(null);

export function PayrollConfigProvider({ children }) {
  const { user, profile } = useAuth();
  const [state, setState] = useState({ versions: [], legacy: {}, loaded: false });

  const reload = useCallback(async () => {
    const isAdmin = profile?.role === "admin";
    const [versions, bonus, tax, cost] = await Promise.all([
      getPayrollConfigs({ includeCost: isAdmin }).catch(() => []),
      getBonusConfig().catch(() => null),
      getTaxConfig().catch(() => null),
      // Chi phí agency chỉ admin đọc được — vai trò khác bỏ qua, họ không dùng tới
      isAdmin ? getCompanyCostConfig().catch(() => null) : Promise.resolve(null),
    ]);
    setState({ versions, legacy: { bonus, tax, cost }, loaded: true });
  }, [profile?.role]);

  useEffect(() => { if (user && profile) reload(); }, [user, profile, reload]);

  const cfgFor = useCallback(
    (month) => resolvePayrollConfig(state.versions, state.legacy, month),
    [state.versions, state.legacy],
  );

  // Quy tắc chung (ngày chốt sổ, giới hạn giải trình, giờ nhắc) theo tháng hiện tại
  const rules = useMemo(
    () => cfgFor(format(new Date(), "yyyy-MM")).rules || DEFAULT_RULES,
    [cfgFor],
  );
  useEffect(() => { setClosingDay(rules.closingDay); }, [rules.closingDay]);

  const value = useMemo(() => ({ ...state, cfgFor, rules, reload }), [state, cfgFor, rules, reload]);
  return <PayrollConfigContext.Provider value={value}>{children}</PayrollConfigContext.Provider>;
}

export const usePayrollConfig = () => useContext(PayrollConfigContext);
